import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '../api/client';
import { useApi } from './provider';
import { authKeys } from './auth';

export const organizationKeys = {
  current: ['organization', 'current'] as const,
};

export const organizationQueryOptions = (api: ApiClient) =>
  queryOptions({
    queryKey: organizationKeys.current,
    // Org metadata (name + currency) is essentially fixed per
    // session — onboarding is the only writer for v1.
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/v1/organizations/current');
      if (response.status === 404) return null;
      if (error) throw error;
      return data ?? null;
    },
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status === 404) return false;
      return failureCount < 2;
    },
  });

export const useOrganization = () => {
  const api = useApi();
  return useQuery(organizationQueryOptions(api));
};

export interface CreateOrganizationInput {
  organization_name: string;
  workspace_name: string;
  currency?: string;
}

export const useCreateOrganization = () => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateOrganizationInput) => {
      const { data, error } = await api.POST('/v1/onboarding/organizations', { body: input });
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: organizationKeys.current });
      await qc.invalidateQueries({ queryKey: authKeys.me });
      await qc.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
};
